import React, { useState, useMemo, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { formatDateTime, dayKeyDR } from '../utils/formatDate';

// "Tabla de Producción" on the admin dashboard: the fillings of the month picked on the card (its own selector, not the
// dashboard's), newest first, searchable (read-only — the filler's screen has the full table with notes and edits,
// productionTable.js). `fillings` is what adminDashboard.js already
// holds; nothing is fetched here. Admin adjustments ("ADJ-") are listed too, marked as such.
const PAGE_SIZES = [5, 10, 25];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
// The Dominican calendar's "YYYY-MM" of a filling / of today.
const monthKey = (value) => dayKeyDR(value).slice(0, 7);
const isAdjustment = (f) => typeof f.fillingID === 'string' && f.fillingID.startsWith('ADJ-');
const when = (f) => f.endTime || f.startTime || f.date;

const ProductionTableCard = ({ fillings = [] }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PAGE_SIZES[0]);
  const today = monthKey(new Date());
  const [month, setMonth] = useState(today.slice(5)); // '01'…'12', or '' = the whole year
  const [year, setYear] = useState(today.slice(0, 4));

  const years = useMemo(() => {
    const found = new Set([today.slice(0, 4)]);
    (Array.isArray(fillings) ? fillings : []).forEach((f) => { if (when(f)) found.add(monthKey(when(f)).slice(0, 4)); });
    return [...found].filter((y) => /^\d{4}$/.test(y)).sort().reverse();
  }, [fillings, today]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const period = month ? `${year}-${month}` : year;
    return (Array.isArray(fillings) ? fillings : [])
      .filter((f) => when(f) && monthKey(when(f)).startsWith(period))
      .filter((f) => !term || [f.fillingID, f.clients, f.completedBy, formatDateTime(when(f))]
        .some((v) => String(v || '').toLowerCase().includes(term)))
      .sort((a, b) => new Date(when(b)) - new Date(when(a)));
  }, [fillings, search, month, year]);

  useEffect(() => { setPage(1); }, [search, fillings, month, year, perPage]);

  const totalPages = Math.max(1, Math.ceil(rows.length / perPage));
  const shown = rows.slice((page - 1) * perPage, page * perPage);
  const totalCylinders = rows.reduce((sum, f) => sum + (Number(f.totalCylinders) || 0), 0);

  return (
    <Card className="mb-4 h-100">
      <Card.Body className="d-flex flex-column">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: '8px' }}>
          <Card.Title className="mb-0">Tabla de Producción</Card.Title>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <select className="form-control form-control-sm" style={{ width: 'auto' }} aria-label="Mes" value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">Todo el año</option>
              {MONTHS.map((name, i) => <option key={name} value={String(i + 1).padStart(2, '0')}>{name}</option>)}
            </select>
            <select className="form-control form-control-sm" style={{ width: 'auto' }} aria-label="Año" value={year} onChange={(e) => setYear(e.target.value)}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: '260px' }}
            placeholder="Buscar cliente, llenador, ID o fecha"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="table-responsive flex-grow-1">
          <table className="table table-bordered table-hover table-sm">
            <thead className="table-light">
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Total</th>
                <th>CC</th>
                <th>PC</th>
                <th>Por</th>
                <th>Min</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    {search.trim() ? 'Ningún llenado coincide con la búsqueda.' : 'Sin llenados en este período.'}
                  </td>
                </tr>
              ) : shown.map((f) => (
                <tr key={f._id || f.fillingID} title={f.fillingID}>
                  <td className="text-nowrap">{formatDateTime(when(f))}</td>
                  <td>{isAdjustment(f) ? <span className="text-mm-cyan">Ajuste Administrativo</span> : f.clients}</td>
                  <td>{f.totalCylinders}</td>
                  <td>{f.ccCount}</td>
                  <td>{f.pcCount}</td>
                  <td>{f.completedBy || '—'}</td>
                  <td>{isAdjustment(f) ? '—' : f.totalTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <small className="text-muted">{rows.length} llenados · {totalCylinders} cilindros</small>
            <select className="form-control form-control-sm" style={{ width: 'auto' }} aria-label="Filas por página" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} por página</option>)}
            </select>
          </div>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
            <small className="text-muted">Página {page} de {totalPages}</small>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ProductionTableCard;
